<script>
import { computed, reactive, ref, watch } from "vue";
import { CognitoUserPool, CognitoUser } from "amazon-cognito-identity-js";
import useAlert from "../../hooks/alert";

//imports userpool data from config
import { POOL_DATA } from "../../config/cognito";
import { useRouter } from "vue-router";
import WawButton from "../../components/ui/Button.vue";
import { useStore } from "vuex";
import { useVuelidate } from '@vuelidate/core'
import { required, email, minLength, sameAs, helpers } from '@vuelidate/validators'
import AtomButton from "@Atoms/Button.vue";
import UserIcon from "@/components/svg/User.vue";
import { evaluateStrength } from '@/utils/passwordStrength';
import { XCircleIcon, CheckCircleIcon } from '@heroicons/vue/24/solid';
import {APP} from "@/config/app";
import MyWealthInvest from "@/components/layout/MyWealthInvest.vue";
import MyWealth from "@/components/layout/MyWealth.vue";
import BaseMessage from "@/components/ui/BaseMessage.vue";

export default {
    computed: {
        APP() {
            return APP
        }
    },
  components: {
    BaseMessage,
    MyWealth,
    MyWealthInvest,
    UserIcon,
    AtomButton,
    WawButton,
    XCircleIcon,
    CheckCircleIcon,
  },
  setup() {
    //get access to Vuex router
    const router = useRouter();

    // create a reference to Vuex store
    const store = useStore();

    // reactive reference to data input fields
    const forgotPasswordForm = reactive({
      username: ''
    })
    // set the validation rules
    const forgotPasswordFormRules = computed(() => {
      return {
        username: {
          required: helpers.withMessage('Please enter your username', required),
          email: helpers.withMessage('Please enter a valid email address', email),
        }
      }
    })
    const v$ = useVuelidate(forgotPasswordFormRules, forgotPasswordForm);

    // stores reactive ref to password inputs
    const resetPasswordForm = reactive({
      code: '',
      newPassword: '',
      confirmNewPassword: ''
    })
    // set the validation rules
    const cognitoPassword = helpers.regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.\[\]{}\(\)?\-\"!@#%&\/,><\':;|_~`])\S{8,99}$/);

    const resetPasswordFormRules = computed(() => {
      return {
        code: {
          required: helpers.withMessage('Please enter the code you received via email.', required),
        },
        newPassword: {
          required: helpers.withMessage('Please enter your new password', required),
          cognitoPassword: helpers.withMessage('Please enter a valid password at least 8 characters in length consisting of at least one uppercase letter, one lowercase letter, one number and one special character', cognitoPassword)
        },
        confirmNewPassword: {
          required: helpers.withMessage('Please enter your new password again', required),
          sameAs: sameAs(resetPasswordForm.newPassword),
        },
      }
    })
    const v2$ = useVuelidate(resetPasswordFormRules, resetPasswordForm);


    const confirmCode = ref(false);

    //sets up hook for message alerting
    const { message, messageStyleType, setMessage } = useAlert();

    //sends confirmation code to email you registered with
    async function sendCode() {
      // runs form validation code
      const validationResult = await v$.value.$validate();
      if(!validationResult){
        return;
      }

      await store.dispatch("setIsLoading", true);

      const userPool = new CognitoUserPool(POOL_DATA);
      const userData = {
        Username: forgotPasswordForm.username,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.forgotPassword({
        onSuccess: function(data) {
          // successfully initiated reset password request
          store.dispatch("setIsLoading", false);
          confirmCode.value = true;
        },
        onFailure: function(err) {
          store.dispatch("setIsLoading", false);
          setMessage(err.message, "danger");
        },
      });
    }

    // performs the actual password reset
    async function resetPassword() {
      // runs form validation code
      const validationResult = await v2$.value.$validate();
      if(!validationResult){
        return;
      }

      await store.dispatch("setIsLoading", true);

      const userPool = new CognitoUserPool(POOL_DATA);
      const userData = {
        Username: forgotPasswordForm.username,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.confirmPassword(resetPasswordForm.code, resetPasswordForm.newPassword, {
        onSuccess() {
          router.push({
            name: "Login",
            query: { passwordResetSuccess: true }, //You have successfully reset your password. Please log in to continue.
          });
          store.dispatch("setIsLoading", false);
        },
        onFailure(err) {
          store.dispatch("setIsLoading", false);
          setMessage(err.message, "danger");
        },
      });
    }

    const isLoading = computed(function() {
      return store.getters.getIsLoading;
    });

    /* Password strength */
    const passwordChecks = reactive({
      characters: false,
      numbers: false,
      uppercase: false,
      lowercase: false,
      special: false
    })

    const passwordStrength = computed(() => {
      return evaluateStrength(resetPasswordForm.newPassword);
    })

    watch(() => resetPasswordForm.newPassword, password => {
      passwordChecks.characters = password.length > 7 && password.length < 51;
      passwordChecks.numbers = /\d/.test(password);
      passwordChecks.uppercase = /[A-Z]/.test(password);
      passwordChecks.lowercase = /[a-z]/.test(password);
      passwordChecks.special = /[\^$*.[\]{}()?"!@#%&\/\\,><':;|_~`=+\- ]/.test(password);
    })

    return {
      forgotPasswordForm,
      resetPasswordForm,
      sendCode,
      resetPassword,
      confirmCode,
      message,
      messageStyleType,
      isLoading,
      v$,
      v2$,
      passwordChecks,
      passwordStrength,
    };
  },
};
</script>

<template>
  <section class="bg-sandstone py-20 lg:py-36 px-8 xl:px-0">
    <div class="mx-auto max-w-7xl flex flex-col items-center justify-center gap-20">
      <div class="text-center flex flex-col gap-12">
        <h1>{{ confirmCode ? 'Change Password' : 'Forgot Password'}}</h1>

        <p class="body-1-light">{{ confirmCode ? 'Please enter the security code you received via email into the field below and enter your new password.' : 'Just let us know your email address and we will email you a security code and link that will allow you to choose a new one.' }}</p>
      </div>

      <base-message :type="messageStyleType" v-if="message">{{ message }}</base-message>

      <div class="flex flex-col space-y-8 lg:w-1/3">
        <div v-if="!confirmCode">
          <form @submit.prevent="sendCode" v-show="!confirmCode" class="flex flex-col space-y-8">
            <div>
              <input
                type="text"
                id="username"
                v-model.trim="forgotPasswordForm.username"
                autocomplete="false"
                class="login-form-input body-3-reg"
                placeholder="Email"
                required
              />
              <div v-if="v$.username.$errors.length" class="my-3 rounded-l bg-red-50 p-3">
                <span v-for="error in v$.username.$errors" :key="error.$uid" class="text-red-400">
                  {{ error.$message }}
                </span>
              </div>
            </div>

            <div class="flex justify-center">
              <AtomButton
                :isLoading="isLoading"
                type="submit"
                aria-label="reset password code button"
                :class="[APP.MyWealthMode ? 'primary-mwp' : 'primary-mwi', 'button-padding body-3-semi-bold rounded-full max-w-max']"
                button-type="unset"
              >
                Send Password Reset Code
              </AtomButton>
            </div>
          </form>
        </div>

        <div v-if="confirmCode">
          <form @submit.prevent="resetPassword" v-show="confirmCode" class="flex flex-col space-y-8">
            <div>
              <div class="mt-2">
                <input
                  type="text"
                  id="code"
                  v-model.trim="resetPasswordForm.code"
                  autocomplete="false"
                  class="login-form-input body-3-reg"
                  placeholder="Code"
                  required
                />
              </div>
              <div v-if="v2$.code.$errors.length" class="my-3 rounded-l bg-red-50 p-3">
                <span v-for="error in v2$.code.$errors" :key="error.$uid" class="text-red-400">
                  {{ error.$message }}
                </span>
              </div>
            </div>

            <div>
              <input
                type="password"
                id="password"
                v-model.trim="resetPasswordForm.newPassword"
                autocomplete="false"
                class="login-form-input body-3-reg"
                placeholder="Password"
                required
              />

              <div v-if="v2$.newPassword.$errors.length" class="my-3 rounded-l bg-red-50 p-3">
                <span v-for="error in v2$.newPassword.$errors" :key="error.$uid" class="text-red-400">
                  {{ error.$message }}
                </span>
              </div>
            </div>

            <div>
              <input
                type="password"
                id="confirm-password"
                v-model.trim="resetPasswordForm.confirmNewPassword"
                autocomplete="false"
                class="login-form-input body-3-reg"
                placeholder="Confirm password"
                required
              />
              <div v-if="v2$.confirmNewPassword.$errors.length" class="my-3 rounded-l bg-red-50 p-3">
                <span v-for="error in v2$.confirmNewPassword.$errors" :key="error.$uid" class="text-red-400">
                  {{ error.$message }}
                </span>
              </div>
            </div>

            <div ref="passwordFilters" class="flex flex-col gap-2 body-3-light">
              <div class="flex items-center border rounded-md border-gray-300 justify-start overflow-hidden">
                <Transition name="width">
                  <div
                    :class="passwordStrength[0] === 1 ? 'w-[10%] bg-red-400' : passwordStrength[0] === 2 ? 'w-[20%] bg-red-400' : passwordStrength[0] === 3 ? 'w-[30%] bg-red-400' : passwordStrength[0] === 4 ? 'w-[40%] bg-red-400' : passwordStrength[0] === 5 ? 'w-[50%] bg-yellow-400' : passwordStrength[0] === 6 ? 'w-[60%] bg-yellow-400' : passwordStrength[0] === 7 ? 'w-[70%] bg-green-400' : passwordStrength[0] === 8 ? 'w-[80%] bg-green-400' : passwordStrength[0] === 9 ? 'w-full bg-green-600' : 'w-full bg-green-600'"
                    class="p-2 h-[40px] body-3-light transition-width ease-in-out duration-300 text-off-black"
                  >
                    <p class="absolute">
                      {{passwordStrength[1]}}
                    </p>
                  </div>
                </Transition>
              </div>
              <div class="flex items-center gap-2 dropdown-content">
                <CheckCircleIcon v-if="passwordChecks.characters" class="w-4 text-green-500" />
                <XCircleIcon v-else class="w-4" />
                <p>8 to 50 characters.</p>
              </div>
              <div class="flex items-center gap-2 dropdown-content">
                <CheckCircleIcon v-if="passwordChecks.numbers" class="w-4 text-green-500" />
                <XCircleIcon v-else class="w-4" />
                <p>1 number or more.</p>
              </div>
              <div class="flex items-center gap-2 dropdown-content">
                <CheckCircleIcon v-if="passwordChecks.uppercase" class="w-4 text-green-500" />
                <XCircleIcon v-else class="w-4" />
                <p>1 upper case letter or more.</p>
              </div>
              <div class="flex items-center gap-2 dropdown-content">
                <CheckCircleIcon v-if="passwordChecks.lowercase" class="w-4 text-green-500" />
                <XCircleIcon v-else class="w-4" />
                <p>1 lower case letter or more.</p>
              </div>
              <div class="flex items-center gap-2 dropdown-content">
                <CheckCircleIcon v-if="passwordChecks.special" class="w-4 text-green-500" />
                <XCircleIcon v-else class="w-4" />
                <p>1 special character or more. Allowed characters are:<br/> ^ $ * . [ ] { } ( ) ? " ! @ # % &amp; / \ , &gt; &lt; ' : ; | _ ~ ` = + - </p>
              </div>
            </div>

            <div class="flex justify-center mt-4">
              <AtomButton
                :isLoading="isLoading"
                type="submit"
                aria-label="change password button"
                button-type="unset"
                :class="[APP.MyWealthMode ? 'primary-mwp' : 'primary-mwi', 'button-padding body-3-semi-bold rounded-full max-w-max']"
              >
                Change Password
              </AtomButton>
            </div>
          </form>
        </div>

        <MyWealthInvest>
          <p class="body-3-light text-center">
            Remembered your password?
            <RouterLink
              to="/login"
              aria-label="Log in button"
              :class="[APP.MyWealthMode ? 'tertiary-mwp' : 'tertiary-mwi', 'body-3-semi-bold underline']"
            >
              Register here
            </RouterLink>
          </p>
        </MyWealthInvest>
        <MyWealth>
          <p class="body-3-light text-center">
            Remembered your password?
            <RouterLink
              to="/login"
              aria-label="Log in button"
              :class="[APP.MyWealthMode ? 'tertiary-mwp' : 'tertiary-mwi', 'body-3-semi-bold underline']"
            >
              Log in
            </RouterLink>
          </p>
        </MyWealth>
      </div>
    </div>
  </section>
</template>
